// Treasury Balances — desk-side inventory, grouped the operator's way.
// Groups come from the BFF (docs/CUTOVER-WORKSHEET.md view directive):
// Office cash (per-currency lines) · Treasury crypto · UAE banks · India banks ·
// India cash · Exchanges · Acquired accounts · Other. Balance-sheet assets stay
// off this screen.
//
// Treasury crypto reads WALLET-FIRST since 4 Sept 2026: one row per wallet
// carrying what the whole wallet is worth in USDT, opening to the coins it
// actually holds at their real quantities. See "THE WALLET ROLL-UP" below for
// the operator's words and for why the transpose applies to the group rather
// than to the one device that prompted it. Every other group is unchanged — an
// office cash box is still counted per currency, a bank is still an account.
//
// Below lg: collapsible cards, same patterns as before. lg+: console layout —
// page header with pocket totals, then one panel per group.
//
// Minimised 14 Sept 2026 (sweep §5.20 / item 60): the valuation source and
// stamp — "Estimate · the Ledger device screen · 04 Sep, 14:32 · 8d ago" —
// was printed under the totals strip, under every group header, under every
// wallet row and per coin, up to a dozen times on one screen. It is said ONCE,
// in "About this screen"; under a figure the note is "est. · 8d ago", amber
// when stale. Per-coin captions and the quiet toggle are short; the banner is
// one sentence. The by-kind fallback layout — 260 lines of a second layout
// with its own words — is deleted: the grouped shape has been served since
// 4 Sept, and a worker answering in the old shape now says so in one line
// instead of rendering a second screen.
//
// 21 Oct 2026 audit (operator, overnight: "I need clean view for operator"): the screen says each
// thing once. "est." is said ONCE, in the totals strip; a group header or a wallet row wears a marker
// only when its figure rests on a stated price older than the freshness window — amber, the price's
// age in full (a week old and more too: fmtAge says nothing past seven days). Estimates read to the
// whole USDT. A group whose one row is on screen under it does not repeat that row's total. Money
// waiting for a booking is ONE amber line above the groups ("To book: …"), built from the very rows
// that carry the reading; the row's own caption stands under its name. With the rate book down the
// banner says so once, and a wallet row reads a quiet dash instead of "rates unread" five times.
//
// THE OUTSIDE READING (19 Sept 2026): an account the books hold nothing on
// shows in its group at 0 when the chain or the venue shows money there —
// "hot wallet base not visible here & maybe many similar places". The rule is
// the BFF's (routes/treasury.ts, Outside); here it is one muted caption
// beside the 0 ("156,742.00 USDC on chain, not yet booked"), the same words
// under a coin, on a wallet row and on an exchange line. Such a row is never
// folded as quiet: it is the opposite of quiet — money waiting.
//
// 21 Oct 2026 design-system sweep: every state is ONE line. The rate-book and stated-price
// banners are "Couldn't read the rate book — no wallet totals." / "Couldn't read stated
// prices — untraded coins show no value." with the reason (and "every quantity below is
// exact", "a failed read, not a zero") behind the tap; the To-book line is the first
// reading and "+N more", every account behind "Details"; a failed read and the old-shape
// answer keep the title and are a red line with "Try again"; an empty desk says "Nothing
// held." once, not in a Totals card as well.

import { Fragment, createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { readWords } from '../lib/errors';
import { ActionBar } from '../components/ActionBar';
import { useQuery } from '@tanstack/react-query';
import { DeskSwitch, useDesk, DESK_LABEL } from '../components/DeskSwitch';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Building2, Landmark, Banknote, Archive, Users2, Coins, History } from 'lucide-react';
import {
  api,
  type TreasuryGroup,
  type TreasuryGroupLine as GroupLine, type TreasuryGroupSource as GroupSource,
  type TreasuryRailValue, type TreasuryRailAsset, type TreasuryValuation,
  type StatedValuation, type TreasuryOutside,
} from '../lib/api';
import { displayAsset, fmtAge, fmtAmount, fmtDate, fmtQuantity, collapseTotals } from '../lib/format';
// The desk's own fiat set — the same one the BFF classifies accounts with.
// Which currencies stay itemised is not a Treasury opinion, so it is not a
// Treasury table.
import { isFiat } from '../lib/workflow';
import {
  railName, railSubtitle, isStale, partitionByRelevance, compareRelevance,
  foldAsset, sameAsset, valuationSourceName, valuationBasisName, owedToRunningAccountWords,
} from '../lib/naming';
import { Name } from '../components/Name';
import { Section } from '../components/Section';
import { KeyValue, KeyValueRow } from '../components/KeyValue';
import { ScreenHeader } from '../components/ScreenHeader';
import { Card, EmptyState, Pending, StateLine } from '../components/kit';
import { walletOrder } from '../components/v2/treasury/logic';

const GROUP_ICONS: Record<string, React.ReactNode> = {
  office_cash:     <Banknote size={14} />,
  treasury_crypto: <Coins size={14} />,
  uae_banks:       <Landmark size={14} />,
  india_banks:     <Landmark size={14} />,
  india_cash:      <Users2 size={14} />,
  exchanges:       <Building2 size={14} />,
  acquired:        <Archive size={14} />,
};
function groupIcon(id: string) { return GROUP_ICONS[id] ?? <Coins size={14} />; }

/** INTO THE HISTORY OF ONE ACCOUNT — the other half of a treasury figure.
 *
 *  This screen says what is sitting on an account right now. Verification
 *  starts by asking what put it there, and the screen offered no way to ask:
 *  Move money, Record in / out and Adjust balance all WRITE, and nothing read.
 *  One component, used wherever an account is named, so the way into an
 *  account's history is the same on the phone and on the console.
 *
 *  Renders nothing without an account id — a group line carries no account of
 *  its own, and a link that filters to nothing is worse than no link.
 *
 *  21 Oct 2026 audit: this is the account's history DOOR, not a "reading is old"
 *  clock — and fourteen of the same icon down the console was clutter. On the
 *  console (`quiet`) it is drawn when the row is hovered or the link has the
 *  keyboard, and the link is on the row all the same; the phone has no hover
 *  and keeps it drawn. */
function RailHistory({ slug, className = '', quiet = false }: { slug?: string | null; className?: string; quiet?: boolean }) {
  if (!slug) return null;
  return (
    <Link
      to={`/movements?rail=${encodeURIComponent(slug)}`}
      title="Money movements on this account"
      aria-label="Money movements on this account"
      className={`shrink-0 text-tg-hint hover:text-tg-accent ${quiet ? 'opacity-0 transition-opacity group-hover/row:opacity-100 focus-visible:opacity-100' : 'transition-colors'} ${className}`}
    >
      <History size={12} />
    </Link>
  );
}

// ── ONE ACCOUNT, OPENED BY LINK (operator, 21 Oct 2026) ─────────────────────────────────────────────
// A desk account has no party page any more — the Parties list, the command palette, the Wallet register's link and a
// typed /parties/<account> all land HERE, as /treasury?rail=<the account's treasury slug>. The account's group opens
// (and its quiet fold), its row is scrolled to and marked with the Books' gold edge, and a wallet or a per-asset line
// unfolds; the history icon is on the row. What the operator keeps folded is not changed by a link.
const FocusRail = createContext<string | null>(null);
const FOCUS_ROW = 'relative bg-brand-gold/10';
/** The gold edge of the row a link asked for. A child bar, not a border: a parent's divide-y repaints the border of every
 *  child after the first, so a border-l-brand-gold would show on the first row only. */
function FocusBar() {
  return <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-brand-gold" />;
}

/** Is this row the one a link asked for? One hook for every row, console and phone, so they agree. */
function useFocusRow(slug: string | null | undefined) {
  const want = useContext(FocusRail);
  const focused = !!want && slug === want;
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focused]);
  return { focused, ref };
}

/** Where in a group the asked-for account sits: on show, behind the quiet fold, or not in it. A per-asset line holds an
 *  account through its sources — the account appears under every coin it holds. */
function whereIs(group: TreasuryGroup, valuation: TreasuryValuation | undefined, slug: string): 'shown' | 'quiet' | null {
  if (readsAsWallets(group, valuation)) {
    const r = splitRails(group);
    return r.shown.some((x) => x.slug === slug) ? 'shown' : r.quiet.some((x) => x.slug === slug) ? 'quiet' : null;
  }
  const l = splitLines(group);
  const has = (x: GroupLine) => x.slug === slug || (x.sources ?? []).some((s) => s.slug === slug);
  return l.shown.some(has) ? 'shown' : l.quiet.some(has) ? 'quiet' : null;
}

/** A plain row that knows whether a link asked for it: marked, and scrolled to. */
function FocusDiv({ slug, className = '', focusClassName = '', children }: { slug?: string | null; className?: string; focusClassName?: string; children: React.ReactNode }) {
  const { focused, ref } = useFocusRow(slug);
  return <div ref={ref} className={`${className} ${focused ? `${FOCUS_ROW} ${focusClassName}` : ''}`}>{focused && <FocusBar />}{children}</div>;
}

// Low-traffic groups start folded — on BOTH layouts now. The phone folded these
// from the start; the console did not, which is why a desk with 31 India bank
// accounts opened as one long wall (operator, 2026-08-17: "all the groups
// should be collapsable for cleaner view").
const COLLAPSED_BY_DEFAULT = new Set(['acquired', 'other']);

const COLLAPSE_KEY = 'dubaiotc_treasury_collapsed';

/**
 * Which groups are folded, remembered across visits.
 *
 * Persisted deliberately: a view the operator has to re-tidy every visit is not
 * a cleaner view. Held ONE level above both layouts so the phone and the console
 * cannot drift into disagreeing about what is folded.
 *
 * Collapsed groups keep their count and subtotal in the header, so folding never
 * hides money — it hides ROWS. That is what makes defaulting some groups closed
 * safe rather than misleading.
 */
function useCollapsedGroups() {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_KEY);
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch {
      // Private mode, or a stale value from an older shape. Fall back to the
      // default rather than throwing on a read-only preference.
    }
    return new Set(COLLAPSED_BY_DEFAULT);
  });

  const persist = (next: Set<string>) => {
    setCollapsed(next);
    try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next])); } catch { /* preference only */ }
  };

  return {
    isCollapsed: (id: string) => collapsed.has(id),
    toggle: (id: string) => {
      const next = new Set(collapsed);
      if (!next.delete(id)) next.add(id);
      persist(next);
    },
    setAll: (ids: string[], collapse: boolean) => persist(collapse ? new Set(ids) : new Set()),
    allCollapsed: (ids: string[]) => ids.length > 0 && ids.every((i) => collapsed.has(i)),
  };
}

/** Fold per-asset lines whose display label collapses to the same thing
 *  (legacy USDTTRC20 postings + canonical USDTTRC → one USDT·TRC line).
 *  Labels come straight from displayAsset(). */
function foldAssetLines(lines: GroupLine[]): GroupLine[] {
  const out: GroupLine[] = [];
  const byLabel = new Map<string, GroupLine>();
  for (const l of lines) {
    const k = displayAsset(l.asset);
    const prev = byLabel.get(k);
    if (!prev) {
      const copy = { ...l, sources: [...(l.sources ?? [])] };
      byLabel.set(k, copy);
      out.push(copy);
    } else {
      prev.balance = (prev.balance ?? 0) + (l.balance ?? 0);
      prev.sources = [...(prev.sources ?? []), ...(l.sources ?? [])];
      if (!prev.rate && l.rate) { prev.rate = l.rate; prev.rate_quote = l.rate_quote; }
    }
  }
  return out;
}

/** Drill-down wallets for a per-asset line, ordered by relevance. The parent
 *  line's asset rides along so the per-asset dust floor applies (a BTC wallet
 *  is not dust at 0.5). */
function drillSources(line: GroupLine): Array<GroupSource & { asset: string | undefined }> {
  return (line.sources ?? [])
    .map((s) => ({ ...s, asset: line.asset }))
    .sort(compareRelevance);
}

/** Quantity × rate, only when the BFF surfaced a stamped rate. Display-layer
 *  math on purpose — the ledger records crypto as quantity (BTC especially).
 *
 *  Named `stamped…` since 4 Sept: this is the rate a POSTING carried in its
 *  metadata, which is a different thing from the rate book the roll-up below
 *  reads, and the two must not answer to the same word in one file. */
function stampedValuation(line: GroupLine): { amount: number; quote?: string } | null {
  if (!line.rate || line.balance === undefined) return null;
  const r = Number(line.rate);
  if (!Number.isFinite(r) || r <= 0) return null;
  return { amount: line.balance * r, quote: line.rate_quote };
}
function fmtStampedValuation(v: { amount: number; quote?: string }): string {
  return `≈ ${fmtAmount(v.amount, v.quote)}${v.quote ? ` ${v.quote}` : ''}`;
}

/* ════════════════════ THE WALLET ROLL-UP ════════════════════
 *
 * Operator, 4 Sept 2026, after his Ledger hardware wallet was booked as the six
 * coins it actually holds — replacing a placeholder that had been the device's
 * whole VALUE typed into the USDT box — and became six top-level rows here:
 * "I don't like this. Hide all these tokens under my ledger party and only show
 * cumulative balance on this screen in USDT."
 *
 * He thinks of that device as one object in a drawer, and he is right to. So
 * Treasury crypto is read WALLET-FIRST: one row per wallet carrying what the
 * whole wallet is worth, opening to the coins with their real quantities.
 *
 * WHY THE WHOLE GROUP AND NOT JUST HIS DEVICE. A rule that fires for one
 * hard-coded wallet has to be re-remembered for the next one (operator, 1 Sept:
 * "hard code those rules projectwise… don't chase single pages"). And the
 * alternative — leaving the per-asset lines up and lifting my-ledger out of
 * them — silently re-bases every line it touches. Transposing the whole group
 * re-bases nothing. Every figure on screen stays the complete truth about the
 * thing it names.
 *
 * WHY NOT OFFICE CASH. Currencies do not roll up. A cash box is counted in its
 * own currency, and "≈ 345,085 USDT of office cash" answers no question this
 * desk asks. Crypto is where one object holds six differently-priced things,
 * which is the problem the roll-up exists to solve.
 *
 * NOTHING IS DELETED. The group's per-asset totals stay in the header where
 * they already were. The quantities are one tap away and are shown EXACTLY
 * (`fmtQuantity`, not the 2dp balance format), because the roll-up is the
 * estimate and the quantities are the reading it has to prove itself against.
 */
const WALLET_FIRST_GROUPS = new Set(['treasury_crypto']);

/** When a stated price stops looking fresh.
 *
 *  Six hours, and the reason is what these prices ARE: numbers read off a
 *  hardware-wallet screen at a moment in time. One nobody has re-read since
 *  before lunch is not a price to settle against. The age is printed beside
 *  every valued figure regardless — this constant only decides when it turns
 *  warn-coloured, so a stale estimate cannot pass for a fresh one at a glance. */
const STALE_AFTER_HOURS = 6;

/** An account shaped for lib/naming. Offering `assets` as `balances` is the
 *  whole adapter: `TreasuryRailValue` then satisfies `RailLike` structurally,
 *  so `railName`, `railSubtitle` and `partitionByRelevance` all work on it
 *  with no cast and no second comparator. */
type RailRow = TreasuryRailValue & { balances: TreasuryRailAsset[] };
function railRow(rv: TreasuryRailValue): RailRow {
  return { ...rv, balances: rv.assets };
}

/* ════════════════════ THE OUTSIDE READING ════════════════════
 *
 * Operator, 19 Sept 2026, on this screen, the day Hot wallet · Base was
 * opened with 156,742 USDC and 0.0397 ETH sitting on the chain as pending
 * rows and nothing booked yet: "hot wallet base not visible here & maybe many
 * similar places". Every group is built from ledger accounts, so an account
 * with nothing posted was not here at all — and "nothing booked yet" read
 * exactly like "nothing there".
 *
 * The rule lives in the BFF (routes/treasury.ts, Outside) and reaches here
 * as `outside` on a coin, a source or a balance: the books hold 0, the chain
 * or the venue shows money. This screen adds ONE muted caption beside the 0
 * and nothing else — no box, no paragraph — and never folds such a row as
 * quiet. The figure is not priced and not in any subtotal: the books' total
 * stays the books' total.
 *
 * 21 Oct 2026 audit: "nothing else" no longer holds — the same readings are
 * also ONE amber "To book" line above the groups (below), because a grey
 * caption at the foot of a row is not where money waiting is looked for. The
 * caption stays, on a line of its own under the account's name.
 */
interface OutsideLike { balance: number; outside?: TreasuryOutside | null }
/** A line at 0 with money waiting outside the books. */
function isOutsideOnly(x: OutsideLike): boolean {
  return x.balance === 0 && !!x.outside;
}
/** The one caption: "156,742.00 USDC on chain, not yet booked" — the same
 *  format as the balance it sits beside, the BFF's words for where it is. */
function outsideCaption(o: TreasuryOutside, asset: string | undefined): string {
  return `${fmtAmount(o.amount, asset)} ${displayAsset(asset)} ${o.words}`;
}
/** Does this wallet row / account line carry money waiting outside? */
function waitingOutside(x: { balances?: readonly OutsideLike[] | null; sources?: readonly OutsideLike[] | null }): boolean {
  return [...(x.balances ?? []), ...(x.sources ?? [])].some(isOutsideOnly);
}
/** One coin of an account that sits at 0 on the books with money waiting outside. */
interface Waiting { asset: string; outside: TreasuryOutside }
/** The coins of a wallet that wait for a booking, in the Worker's order (the
 *  larger reading first). */
function waitingCoins(rail: RailRow): Waiting[] {
  return rail.assets.filter(isOutsideOnly).map((a) => ({ asset: a.asset, outside: a.outside! }));
}
/** "48,250 USDC" — one waiting coin, as the caption and the To-book line both say it. */
function waitingAmount(w: Waiting): string {
  return `${fmtAmount(w.outside.amount, w.asset)} ${displayAsset(w.asset)}`;
}
/** The caption under an account's name: "48,250 USDC on chain, not yet booked".
 *  An account with several coins waiting says the Worker's words once —
 *  "48,250 USDC · 0.0397 ETH on chain, not yet booked".
 *
 *  21 Oct 2026 audit: the caption has a line of its own under the name (it sat
 *  under the figure, in the column that squeezed the name to "Hot wallet · …"),
 *  and it names every coin waiting, not only the first — the same readings the
 *  To-book line above the groups lists. */
function waitingCaption(items: readonly Waiting[]): string {
  const byWords = new Map<string, string[]>();
  for (const w of items) {
    const amounts = byWords.get(w.outside.words) ?? [];
    amounts.push(waitingAmount(w));
    byWords.set(w.outside.words, amounts);
  }
  return [...byWords].map(([words, amounts]) => `${amounts.join(' · ')} ${words}`).join('; ');
}

/** ONE AMBER LINE ABOVE THE GROUPS (21 Oct 2026 audit). Money waiting for a
 *  booking sat as a 10.5px grey caption on the fifth wallet row and at the foot
 *  of the exchanges — "Hot wallet · Base ≈ 0 USDT" is not where a man looks for
 *  48,250 USDC. The line is built from the rows that already carry the reading
 *  (`outside`, the same data their captions read — no new field, no read of its
 *  own) and mirrors what each group draws: a wallet group from its wallets, a
 *  per-asset group from the accounts under each coin, an account group from its
 *  balances. No such row, no line. */
interface ToBook { key: string; row: { slug?: string | null; label?: string | null }; waiting: Waiting[] }
function toBook(groups: TreasuryGroup[], valuation?: TreasuryValuation): ToBook[] {
  const out: ToBook[] = [];
  for (const g of groups) {
    if (readsAsWallets(g, valuation)) {
      for (const rv of g.rails ?? []) {
        const waiting = waitingCoins(railRow(rv));
        if (waiting.length > 0) out.push({ key: rv.slug, row: rv, waiting });
      }
    } else if (g.style === 'asset_lines') {
      // An account sits under every coin it holds: gather it once.
      const byAccount = new Map<string, ToBook>();
      for (const l of foldAssetLines(g.lines)) {
        for (const s of l.sources ?? []) {
          if (!isOutsideOnly(s)) continue;
          const entry = byAccount.get(s.slug) ?? { key: s.slug, row: s, waiting: [] };
          entry.waiting.push({ asset: l.asset ?? '', outside: s.outside! });
          byAccount.set(s.slug, entry);
        }
      }
      out.push(...byAccount.values());
    } else {
      for (const l of g.lines) {
        const waiting = (l.balances ?? []).filter(isOutsideOnly).map((b) => ({ asset: b.asset, outside: b.outside! }));
        if (waiting.length > 0) out.push({ key: l.key, row: l, waiting });
      }
    }
  }
  return out;
}
/** Money waiting is the opposite of quiet: whatever relevance made of a row
 *  whose balances are all 0, a row with an outside reading stays on screen.
 *  Order inside `shown` is untouched. */
function keepWaiting<T extends { balances?: readonly OutsideLike[] | null; sources?: readonly OutsideLike[] | null }>(
  p: { shown: T[]; hidden: T[] },
): { shown: T[]; hidden: T[] } {
  const waiting = p.hidden.filter(waitingOutside);
  if (waiting.length === 0) return p;
  return { shown: [...p.shown, ...waiting], hidden: p.hidden.filter((x) => !waitingOutside(x)) };
}
/** An account's state words, except that an account with money waiting
 *  outside never reads "Dormant" or "Quiet": lib/naming judges by the
 *  balances, which are 0 here, so it is asked with them set aside and
 *  answers with the kind alone. */
function accountSubtitle<T extends Parameters<typeof railSubtitle>[0] & { balances?: readonly OutsideLike[] | null }>(
  row: T, opts?: Parameters<typeof railSubtitle>[1],
): string {
  return railSubtitle(waitingOutside(row) ? { ...row, balances: [] } : row, opts);
}

/**
 * Does this group read as wallets?
 *
 * Only when the BFF sent its `valuation` block. A roll-up printed without the
 * note that says how to read it — what the quote is, whether the rate book was
 * even readable — is exactly what the doctrine forbids, so its absence falls
 * back to the per-asset lines rather than to a bare number.
 */
function readsAsWallets(group: TreasuryGroup, valuation?: TreasuryValuation): boolean {
  return !!valuation
    && group.style === 'asset_lines'
    && WALLET_FIRST_GROUPS.has(group.id)
    && (group.rails?.length ?? 0) > 0;
}

/**
 * Same split as `splitLines`, over accounts instead of asset lines — one quiet
 * wallet behind the same disclosure, counted in the same subtotal.
 *
 * Relevance decides WHICH accounts are quiet. It does not decide their ORDER
 * here: its tie-break is the largest |balance| a row carries, which cannot
 * compare 9.90 BTC with 152,082 TRX. The BFF has already ordered these by
 * cumulative value, the one comparison that is meaningful once every account
 * is quoted in the same thing, so its order stands inside each bucket.
 */
function splitRails(group: TreasuryGroup): { rails: RailRow[]; shown: RailRow[]; quiet: RailRow[] } {
  const rails = (group.rails ?? []).map(railRow);
  const { shown, hidden } = keepWaiting(partitionByRelevance(rails, { maxTier: 1 }));
  return { rails, ...walletOrder(rails, shown, hidden) };   // his order (30 Oct 2026): components/v2/treasury/logic.ts
}

/**
 * WHAT KIND OF NUMBER IS THIS?
 *
 * A wallet's cumulative figure is not always a number, and when it is a number
 * it is not always the same kind of number. Decided once, here, so the phone
 * and the console can never say it two different ways:
 *
 *   unavailable  the rate book could not be read. There is no figure, and the
 *                row says why — it never renders as a blank or a 0. (21 Oct
 *                2026 audit: the BANNER says why, once, at the top; the row
 *                reads a quiet dash — five rows each saying "rates unread"
 *                was the one fact five times.)
 *   counted      everything on the account already IS the quote (this desk
 *                holds USDT at USD 1:1), so the roll-up is the same reading as
 *                the quantities beneath it. A count, not a conversion: no "≈".
 *   valued       a conversion. Wears "≈".
 *   partial      something on the account has no price at all, so the total is
 *                a floor, not a total. Wears "≥" and names what is missing.
 */
type RollUpKind = 'unavailable' | 'unpriceable' | 'counted' | 'valued' | 'partial';
interface RollUp {
  kind: RollUpKind;
  amount: number | null;
  quote: string;
  /** Assets on this account with no price — excluded from `amount`, named on screen. */
  unpriced: string[];
  /** Set when any part of the figure came from a stated price (db/0035). */
  stated: { sources: string; as_of: string; age_hours: number | null } | null;
  stale: boolean;
}

function rollUp(rv: TreasuryRailValue, v: TreasuryValuation): RollUp {
  const quote = v.quote;
  const stated = rv.stated_sources.length > 0 && rv.stated_as_of
    ? {
        sources: rv.stated_sources.map(valuationSourceName).join(' · '),
        as_of: rv.stated_as_of,
        age_hours: rv.stated_age_hours,
      }
    : null;
  const stale = stated !== null && (stated.age_hours ?? 0) >= STALE_AFTER_HOURS;
  if (!v.available) {
    return { kind: 'unavailable', amount: null, quote, unpriced: [], stated: null, stale: false };
  }
  if (rv.value_usdt === null) {
    // NOTHING PRICED IS NOT "≥ 0". An account where every asset is unpriceable
    // came back with value_usdt_priced 0, and a "≥ 0.00 USDT" is the one
    // reading a glance takes for an empty wallet. When there is no priced part
    // there is no figure — say that instead.
    return rv.value_usdt_priced > 0
      ? { kind: 'partial', amount: rv.value_usdt_priced, quote, unpriced: rv.unpriced, stated, stale }
      : { kind: 'unpriceable', amount: null, quote, unpriced: rv.unpriced, stated, stale };
  }
  const counted = rv.assets.length > 0 && rv.assets.every((a) => foldAsset(a.asset) === foldAsset(quote));
  return { kind: counted ? 'counted' : 'valued', amount: rv.value_usdt, quote, unpriced: [], stated, stale };
}

/** "≈ 4,967.19", but never "≈ under 0.01".
 *
 *  `fmtAmount` already says "under 0.01" for a value too small to show at its
 *  precision — that is how a non-zero balance is stopped from rendering as a
 *  bare 0. Prefixing that gives "≈ under 0.01", two approximations of one
 *  figure. One approximation mark per figure. */
function approx(text: string, mark = '≈'): string {
  return /^(≈|-?under )/.test(text) ? text : `${mark} ${text}`;
}

/** An ESTIMATE, to the whole unit (21 Oct 2026 audit: "≈ 806,973.2197 USDT" is
 *  four places of precision on a figure that rests on a rate — it read as a
 *  count). DISPLAY ONLY: nothing stored or typed is rounded here. A value under
 *  one keeps the usual places, so a small estimate is never drawn as a bare
 *  "under 0.01". A COUNTED figure (a coin that already is the quote) is not an
 *  estimate and never goes through this. */
function fmtEstimate(amount: number | null, quote: string, lowerBound = false): string {
  if (amount === null) return '—';
  // A LOWER BOUND ("≥", part of it could not be priced) is rounded DOWN: 100.6 priced and a little unpriced is
  // "≥ 100", never "≥ 101" — the true total could be under 101 (Astra review, 21 Oct 2026). Under one the same
  // holds at the places shown: 0.99999 priced is "≥ 0.99", never "≥ 1" (Astra, second pass; two places, rule 10).
  if (lowerBound) {
    if (Math.abs(amount) >= 1) return fmtAmount(Math.floor(amount), quote, 0);
    return fmtAmount(Math.floor(amount * 100) / 100, quote);
  }
  return Math.abs(amount) >= 1 ? fmtAmount(amount, quote, 0) : fmtAmount(amount, quote);
}

/** How old a stated price is, always in words. `fmtAge` says nothing past seven
 *  days — right beside a date that is on screen (it would stutter), wrong here,
 *  where the age IS the warning: a price a week old and more printed no age at
 *  all (21 Oct 2026 audit: "est." and nothing beside it, on the oldest price on
 *  the screen). Past a week it is whole days. */
function ageWords(iso: string, hours?: number | null): string {
  const age = fmtAge(iso);
  if (age) return age;
  const h = hours ?? (Date.now() - Date.parse(iso)) / 3_600_000;
  return Number.isFinite(h) ? `${Math.max(7, Math.round(h / 24))}d ago` : '';
}

/** The cumulative figure for one wallet — or for a whole group, or for the
 *  desk's entire crypto position. Never a bare number: "≈" when it is a
 *  conversion, "≥" when part of it could not be priced, and no figure at all —
 *  with the reason — when the rate book could not be read.
 *
 *  `size` is the only thing a caller varies. The same figure appears at three
 *  weights — a group header line, the totals strip, a wallet row — and three
 *  components saying "≈ N USDT" three ways is three chances for one of them
 *  to forget the "≈". */
const FIGURE_SIZE: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'text-meta font-medium',
  md: 'text-body font-semibold',
  lg: 'text-title font-semibold',
};
function RollUpFigure({ r, size = 'md' }: { r: RollUp; size?: 'sm' | 'md' | 'lg' }) {
  if (r.kind === 'unavailable') {
    // 21 Oct 2026 audit: this said "rates unread" in amber on every wallet row —
    // five times under the one banner that already says it. The banner is the
    // statement; the row reads a quiet dash (never a 0, never blank).
    return <span className="text-tg-hint" title="Rates unread">—</span>;
  }
  if (r.kind === 'unpriceable') {
    return <span className="text-meta text-warn">no price for {r.unpriced.map(displayAsset).join(', ')}</span>;
  }
  // An estimate reads to the whole USDT; a counted figure is exact and stays so.
  const figure = r.kind === 'counted' ? fmtAmount(r.amount, r.quote) : fmtEstimate(r.amount, r.quote, r.kind === 'partial');
  return (
    <span className={`font-mono tabular ${FIGURE_SIZE[size]} ${(r.amount ?? 0) < 0 ? 'text-neg' : ''}`}>
      {r.kind === 'counted' ? figure : approx(figure, r.kind === 'partial' ? '≥' : '≈')}{' '}
      <span className="font-sans text-meta font-normal tracking-[0.02em] text-tg-hint">{r.quote}</span>
    </span>
  );
}

/**
 * THE NOTE UNDER A FIGURE — "est. · 8d ago" (§5.20). A valued figure says it
 * is an estimate and how old its price is; once the price is stale the note
 * turns warn-coloured, so an estimate hours out of date cannot be read at a
 * glance as a fresh one. The source and the stamp are said once, in "About
 * this screen" — not under every figure. An account that could not be fully
 * priced says which assets are missing from the figure.
 *
 * 21 Oct 2026 audit: "est." was printed seven times on one screen (strip, two
 * group headers, four wallet rows) and amber on every figure with a stated
 * price, so it never cleared. Now the STRIP says "est." once (`strip`, with
 * the age of the oldest price), and a group header or a wallet row carries a
 * marker only where its own figure rests on a stated price older than the
 * freshness window — amber, with the age in full. The "≈" in front of the
 * figure stays on every row: it is what says the figure is a valuation, and it
 * is never removed. What could not be valued (the unpriced coins) is always said.
 */
function ValuationNote({ r, strip = false }: { r: RollUp; strip?: boolean }) {
  const parts: Array<{ key: string; className?: string; text: string }> = [];
  if (r.stated && (strip || r.stale)) {
    const age = ageWords(r.stated.as_of, r.stated.age_hours);
    parts.push({ key: 'stated', className: r.stale ? 'text-warn' : undefined, text: age ? `est. · ${age}` : 'est.' });
  } else if (strip && (r.kind === 'valued' || r.kind === 'partial')) {
    parts.push({ key: 'derived', text: 'est.' });
  }
  if (r.unpriced.length > 0) {
    parts.push({ key: 'unpriced', className: 'text-warn', text: `no price for ${r.unpriced.map(displayAsset).join(', ')}` });
  }
  if (parts.length === 0) return null;
  return (
    <span className="block text-meta leading-[1.45] text-tg-hint">
      {parts.map((p, i) => (
        <Fragment key={p.key}>
          {i > 0 && <span className="opacity-40"> · </span>}
          <span className={p.className}>{p.text}</span>
        </Fragment>
      ))}
    </span>
  );
}

/* ════════════════════ THE CRYPTO COLLAPSE ════════════════════
 *
 * Operator, twice, looking at this screen: "Hide all these tokens under my
 * ledger party and only show cumulative balance on this screen in USDT — my
 * Ledger is not holding these diff assets, collapse them into equivalent USDT
 * value for my ledger and show it to me."
 *
 * The wallet roll-up above answered half of that. The other half is the two
 * places that still ENUMERATED every coin the desk owns: the totals strip at
 * the top of the screen, and the per-asset subtotals in every group header —
 * thirteen numbers to answer one question. Both now LEAD with the desk's
 * fiat, itemised, and ONE cumulative figure for everything else.
 *
 * FIAT DOES NOT COLLAPSE. AED, INR and USD are the currencies this desk thinks
 * and settles in, and each is a counted number in its own right. Crypto is
 * where one pocket holds ten differently-priced things.
 *
 * NOTHING IS DELETED AND NOTHING IS RE-BASED. Every quantity is exactly where
 * it was — one tap down, in the wallet rows and the coins under them, at full
 * precision. This changes what the screen LEADS with, not what it holds.
 *
 * AND NOTHING IS INVENTED. The collapse re-quotes figures the BFF already sent
 * and labels them under the same law as the wallet roll-up — same `RollUp`,
 * same `RollUpFigure`, same `ValuationNote`, so it is an estimate wherever any
 * part of it came from a rate, it carries the age of the OLDEST rate feeding
 * it, and it says so out loud when something inside it has no price at all.
 */

/** USDT per ONE unit of an asset, read back out of the accounts.
 *
 *  The BFF prices every holding but publishes the RESULT, not the rate: each
 *  account line carries `balance` and `value_usdt` for every coin on it. One
 *  division gives the rate back, and it is the same rate on every account —
 *  `priceMajor` is a pure function of the asset — so the first account holding
 *  the coin answers for all of them. Keyed by the FOLD, because that is what
 *  the rate book itself is keyed by: USDT-TRC and USDT are one dollar on two
 *  accounts.
 *
 *  A RATE, not a sum of accounts. What has to be collapsed is a TOTALS map —
 *  the pocket totals, a group's subtotals — and a totals map is not always the
 *  sum of the accounts in front of it: one office account can hold cash
 *  counted under Office cash and coins counted under Treasury crypto.
 *  Multiplying the very map the operator is looking at keeps the collapsed
 *  figure equal to the figures it stands in for, whatever the grouping does. */
interface AssetRate { rate: number; stated: StatedValuation | null }
function assetRates(groups: TreasuryGroup[], valuation?: TreasuryValuation): Map<string, AssetRate> {
  const stated = new Map<string, StatedValuation>();
  for (const s of valuation?.stated ?? []) stated.set(foldAsset(s.asset), s);
  const rates = new Map<string, AssetRate>();
  for (const g of groups) {
    for (const rv of g.rails ?? []) {
      for (const a of rv.assets) {
        const fam = foldAsset(a.asset);
        // A zero balance carries no rate (0/0), and an unpriced asset carries
        // none by definition — both leave the asset out, which is what makes it
        // show up as "no price for …" instead of quietly as nothing.
        if (rates.has(fam) || a.value_usdt === null || a.balance === 0) continue;
        rates.set(fam, {
          rate: a.value_usdt / a.balance,
          stated: a.basis === 'stated' ? stated.get(fam) ?? null : null,
        });
      }
    }
  }
  return rates;
}

/**
 * The same roll-up, over a TOTALS MAP instead of one account.
 *
 * Every kind means exactly what it means for a wallet (see `RollUp` above), so
 * a collapsed group header and a wallet row can never describe the same money
 * two different ways.
 */
function collapseAssets(
  amounts: Record<string, number>,
  rates: Map<string, AssetRate>,
  v: TreasuryValuation,
): RollUp {
  const quote = v.quote;
  if (!v.available) {
    return { kind: 'unavailable', amount: null, quote, unpriced: [], stated: null, stale: false };
  }
  const unpriced: string[] = [];
  const sources = new Set<string>();
  let priced = 0;
  let allQuote = true;
  let oldest: StatedValuation | null = null;
  for (const [asset, amount] of Object.entries(amounts)) {
    if (!sameAsset(asset, quote)) allQuote = false;
    const r = rates.get(foldAsset(asset));
    if (!r) { unpriced.push(asset); continue; }
    priced += amount * r.rate;
    if (r.stated) {
      sources.add(valuationSourceName(r.stated.source));
      // The age that matters is the age of the STALEST input, not the freshest
      // — the same rule the BFF applies per account.
      if (oldest === null || r.stated.as_of < oldest.as_of) oldest = r.stated;
    }
  }
  const stated = oldest === null
    ? null
    : { sources: [...sources].sort().join(' · '), as_of: oldest.as_of, age_hours: oldest.age_hours };
  const stale = stated !== null && stated.age_hours >= STALE_AFTER_HOURS;
  if (unpriced.length > 0) {
    // Same distinction the wallet rows make: a figure with a known part is a
    // FLOOR and wears "≥"; a figure with no known part is not a figure at all,
    // because "≥ 0.00 USDT" is what an empty pocket looks like.
    return priced === 0
      ? { kind: 'unpriceable', amount: null, quote, unpriced: unpriced.sort(), stated, stale }
      : { kind: 'partial', amount: priced, quote, unpriced: unpriced.sort(), stated, stale };
  }
  // NOTHING IS NOT ZERO. An empty map came back as a COUNTED 0.00, which reads
  // as "this holds nothing, and we checked" — the same misreading the partial
  // branch above refuses. Say there is nothing to value instead.
  if (!allQuote && priced === 0 && unpriced.length === 0 && Object.keys(amounts).length === 0) {
    return { kind: 'unpriceable', amount: null, quote, unpriced: [], stated, stale };
  }
  return { kind: allQuote ? 'counted' : 'valued', amount: priced, quote, unpriced: [], stated, stale };
}

/** What a totals line LEADS with: the desk's currencies, one figure each, and
 *  everything else as one cumulative estimate. `crypto` is null when there is
 *  no crypto in the map, or when the response cannot say how to read a
 *  collapsed figure — and then `fiat` carries every asset, itemised exactly as
 *  before, because a cumulative figure nobody can qualify is worse than no
 *  cumulative figure (the same fallback `readsAsWallets` makes). */
interface Headline {
  fiat: Array<[string, number]>;
  crypto: RollUp | null;
  /** How many positions went into `crypto` — the operator's assurance that the
   *  coins were folded, not dropped. */
  coins: number;
}
function headline(
  totals: Record<string, number>,
  rates: Map<string, AssetRate>,
  v?: TreasuryValuation,
): Headline {
  const itemised = () => ({ fiat: Object.entries(collapseTotals(totals)).sort(), crypto: null, coins: 0 });
  if (!v || !v.available) return itemised();
  const fiat: Record<string, number> = {};
  const crypto: Record<string, number> = {};
  for (const [asset, amount] of Object.entries(totals)) (isFiat(asset) ? fiat : crypto)[asset] = amount;
  const coins = Object.keys(crypto).length;
  if (coins === 0) return itemised();
  return {
    fiat: Object.entries(collapseTotals(fiat)).sort(),
    crypto: collapseAssets(crypto, rates, v),
    coins,
  };
}

/** "6 coins" — one place, so a wallet row and a collapsed total cannot count
 *  the same holdings two different ways. */
function coinsText(n: number): string { return n === 1 ? '1 coin' : `${n} coins`; }

/** A headline on one line: fiat as it always was, then the collapsed figure.
 *  `note` carries "est. · 8d ago" underneath — dropped only where there is no
 *  room for a second line (a folded card's one-line summary). */
function GroupHeadline({ h, size = 'sm', note = true, className = '' }: {
  h: Headline; size?: 'sm' | 'md'; note?: boolean; className?: string;
}) {
  if (h.fiat.length === 0 && !h.crypto) return null;
  return (
    <span className={className}>
      {h.fiat.length > 0 && (
        <span className="font-mono tabular">
          {h.fiat.map(([a, v]) => `${a} ${fmtAmount(v, a)}`).join(' · ')}
        </span>
      )}
      {h.fiat.length > 0 && h.crypto && <span className="opacity-40"> · </span>}
      {h.crypto && <RollUpFigure r={h.crypto} size={size} />}
      {note && h.crypto && <ValuationNote r={h.crypto} />}
    </span>
  );
}

/** The coins behind a rolled-up wallet — the exact reading the roll-up above is
 *  derived from. Quantities at full precision, and each coin carries its own
 *  value and, in a word, how it was priced (§5.20: "≈ 1,234 USDT · desk's own
 *  trades" · "counted" · "no price"). With the rate book down the banner has
 *  said so once, so a coin says nothing of it (21 Oct 2026 audit: "rates
 *  unread" under every coin of every open wallet); an estimate reads to the
 *  whole USDT, as everywhere on this screen. */
function WalletCoins({ rail, valuation, dense = false }: {
  rail: RailRow; valuation: TreasuryValuation; dense?: boolean;
}) {
  return (
    <div className={dense ? 'pl-5 pb-1' : 'px-4 pb-2.5'}>
      {rail.assets.map((a) => {
        const caption = isOutsideOnly(a)
          // Nothing booked, money waiting: the one caption, no valuation
          // words — there is nothing of the books' to value.
          ? <>{outsideCaption(a.outside!, a.asset)}</>
          : !valuation.available
            ? null
            : a.value_usdt === null
              ? <span className="text-warn">no price</span>
              // A coin that IS the quote is not valued at all, it is counted.
              : foldAsset(a.asset) === foldAsset(valuation.quote)
                ? <>counted</>
                : <>{approx(fmtEstimate(a.value_usdt, valuation.quote))} {valuation.quote} · {valuationBasisName(a.basis)}</>;
        return (
          <div key={a.asset} className={`flex items-baseline justify-between gap-3 py-1 ${dense ? '' : 'pl-6'}`}>
            <span className={`text-meta text-tg-hint`}>{displayAsset(a.asset)}</span>
            <span className="text-right shrink-0">
              <span className={`font-mono tabular text-meta ${a.balance < 0 ? 'text-neg' : ''}`}>
                {fmtQuantity(a.balance, a.asset)}
              </span>
              {caption && <span className="block text-meta leading-[1.45] text-tg-hint">{caption}</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * A FAILED READ IS NOT A SETTLED FACT.
 *
 * If the rate book could not be read, this screen says so at the top — it does
 * not quietly show wallets with no figure beside them, which reads as "these
 * wallets are worth nothing". And "the stated prices could not be read" is a
 * different fact from "these assets have no price": the operator acts
 * differently on each, so they are never reported the same way.
 */
function ValuationBanner({ valuation, onRetry }: { valuation?: TreasuryValuation; onRetry: () => void }) {
  if (!valuation) return null;
  const retry = <button type="button" className="btn-link" onClick={onRetry}>Try again</button>;
  // One line each (sweep, 21 Oct 2026: 14 and 17 words): what failed and what that means; the reason and the
  // reassurance behind the tap.
  if (!valuation.available) {
    const why = valuation.error ? `${valuation.error.charAt(0).toUpperCase()}${valuation.error.slice(1)}${/[.!?]$/.test(valuation.error) ? '' : '.'}` : '';
    return (
      <Card tone="warn" className="bg-warn-bg">
        <StateLine tone="warn" action={retry} more={<>{why && <p>{why}</p>}<p>Every quantity below is exact.</p></>}>
          Couldn&rsquo;t read the rate book — no wallet totals.
        </StateLine>
      </Card>
    );
  }
  if (!valuation.stated_available) {
    return (
      <Card tone="warn" className="bg-warn-bg">
        <StateLine tone="warn" action={retry} more="A failed read, not a zero.">
          Couldn&rsquo;t read stated prices — untraded coins show no value.
        </StateLine>
      </Card>
    );
  }
  return null;
}

/** MONEY WAITING FOR A BOOKING, AS ONE AMBER LINE — "To book: 48,250 USDC on Hot
 *  wallet · Base +2 more" (21 Oct 2026 audit; the data is `toBook`'s). Above the
 *  groups, where it is looked at first; nothing to say, no line. The line says the
 *  first reading and counts the rest (the sweep, 21 Oct 2026: with two accounts it
 *  was 15 words); behind "Details" is every account with every coin waiting on it.
 *  The phone names an account without its own desk in front of it, as its rows do
 *  (`desk` given); the console prints the name whole — the owner prefix is the
 *  account's identification (lib/naming railName). Every row below still carries
 *  its own caption. */
function ToBookLine({ items, desk }: { items: ToBook[]; desk?: string }) {
  if (items.length === 0) return null;
  const name = (it: ToBook) => (desk === undefined ? railName(it.row) : phoneRailName(it.row, desk));
  const readings = items.flatMap((it) => it.waiting.map((w) => ({ it, w })));
  const first = readings[0]!;
  const others = readings.length - 1;
  // A box, as the audit made it: money waiting is what he looks for first (the rim and tint are the kit's alert card).
  return (
    <Card tone="warn" className="bg-warn-bg">
      <StateLine
        tone="warn"
        more={others > 0 ? items.map((it) => <p key={it.key}>{it.waiting.map(waitingAmount).join(' + ')} on {name(it)}</p>) : undefined}
      >
        To book: {waitingAmount(first.w)} on {name(first.it)}{others > 0 ? ` +${others} more` : ''}
      </StateLine>
    </Card>
  );
}

/** Noun for a group's line count — wallets, currencies, assets, or accounts. */
function lineNoun(groupId: string, n: number, wallets = false): string {
  const noun = wallets ? 'wallet'
    : groupId === 'office_cash' ? 'currency'
    : groupId === 'treasury_crypto' ? 'asset'
    : 'account';
  if (n === 1) return noun;
  return noun === 'currency' ? 'currencies' : `${noun}s`;
}

/**
 * Treasury is the one surface that hides NOTHING: stale accounts collapse into
 * an expandable row and stay inside the subtotal, because hiding is a search
 * affordance and never an accounting one. `hidden.length` is the true N.
 */
function splitLines(group: TreasuryGroup): { lines: GroupLine[]; shown: GroupLine[]; quiet: GroupLine[] } {
  const lines = group.style === 'asset_lines' ? foldAssetLines(group.lines) : group.lines;
  // maxTier 1, not 2 (2026-08-13): operator — real CEX accounts were being
  // buried among accounts holding 0.30 USDT. Treasury shows what is LIVE; quiet
  // and stale accounts collapse behind "+ N quiet accounts". Group subtotals
  // still include every line — hiding is a reading affordance, never an
  // accounting one.
  const { shown, hidden } = keepWaiting(partitionByRelevance(lines, { maxTier: 1 }));
  return { lines, shown, quiet: hidden };
}

/**
 * Groups whose every line is stale sink to the bottom; among the rest the
 * worksheet's editorial order stands. Ordering only — no group is ever
 * dropped, so the pocket totals above still add up to what is on screen.
 */
function orderGroups(groups: TreasuryGroup[]): TreasuryGroup[] {
  return groups
    .map((g, i) => ({ g, i, dead: g.lines.length > 0 && g.lines.every(isStale) }))
    .sort((a, b) => (a.dead === b.dead ? a.i - b.i : a.dead ? 1 : -1))
    .map((x) => x.g);
}

/** The mandatory disclosure for anything Treasury collapses. Never a bare
 *  "…" — the operator sees the count before deciding to look. That the
 *  folded rows stay in the subtotal is said once, in About this screen. */
function QuietToggle({ count, noun, open, onToggle }: {
  count: number; noun: string; open: boolean; onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center gap-1.5 px-4 py-2 text-left text-meta text-tg-hint transition-colors hover:text-tg-text"
    >
      {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      {open ? `Hide ${count} quiet ${noun}` : `+ ${count} quiet ${noun}`}
    </button>
  );
}

function countText(groupId: string, n: number, wallets = false): string {
  return `${n} ${lineNoun(groupId, n, wallets)}`;
}

/** THE STAMP, ONCE. The oldest stated price feeding this desk's figures and
 *  the sources they came from — said in About this screen and nowhere else. */
function statedStamp(valuation?: TreasuryValuation): { sources: string; as_of: string; age: string } | null {
  const s = valuation?.stated ?? [];
  if (s.length === 0) return null;
  const oldest = s.reduce((a, b) => (b.as_of < a.as_of ? b : a));
  const sources = [...new Set(s.map((x) => valuationSourceName(x.source)))].sort().join(' · ');
  // The age is always said (ageWords: days past a week, where fmtAge goes silent).
  return { sources, as_of: oldest.as_of, age: ageWords(oldest.as_of, oldest.age_hours) };
}

export function TreasuryBalances() {
  // Which desk — DubaiOTC only since the IndiaOTC wind-down (26 Sept 2026,
  // components/DeskSwitch.tsx). `useDesk()` stays: a second desk widens one
  // line there, not this screen.
  const [desk, setDesk] = useDesk();
  const q = useQuery({ queryKey: ['treasury', 'balances', desk], queryFn: () => api.treasuryBalances(desk) });
  // Called BEFORE the early returns — a hook behind a conditional return is a
  // hook-order violation, and this screen returns early three times.
  const fold = useCollapsedGroups();
  // The account a link asked to open (/treasury?rail=<slug>) — see FocusRail.
  const [params] = useSearchParams();
  const focus = params.get('rail');

  // While there is nothing to draw — reading, or a read that failed — the title stays (sweep, 21 Oct 2026: the
  // screen was a bare red line with no name).
  const retry = <button type="button" className="btn-link" onClick={() => q.refetch()}>Try again</button>;
  const frame = (body: ReactNode) => (
    <div className="space-y-4">
      <ScreenHeader title="Wallets" actions={<DeskSwitch value={desk} onChange={setDesk} />} />
      {body}
    </div>
  );
  // Error first, then the data guard. Written the other way round, an errored
  // query shows "Loading…" for ever — react-query v5 leaves `data` undefined on
  // error — which is the trap BalanceSheet.tsx already carries a comment about.
  if (q.isError) {
    // A failed read is a line of its own — never the "Nothing held" of an empty desk: what failed, the first
    // sentence of what the books said, the rest behind a tap.
    const said = readWords(q.error);
    const first = said.split(/(?<=[.!?])\s+/)[0] ?? said;
    return frame(
      <StateLine tone="bad" more={said.slice(first.length).trim() || undefined} action={retry}>
        Couldn&rsquo;t read the treasury. {first}
      </StateLine>,
    );
  }
  if (!q.data)   return frame(<Pending what="Reading…" />);

  const data = q.data;
  // The grouped shape has been served since 4 Sept 2026; a worker answering
  // in the old shape is a failed read, said as one, not a second screen.
  if (!data.groups) {
    return frame(
      <StateLine tone="bad" more="The books answered in an old shape. Refresh, or deploy the worker." action={retry}>
        Couldn&rsquo;t read the treasury — old answer from the books.
      </StateLine>,
    );
  }

  // Relevance orders the groups; it never removes one. Totals below are the
  // BFF's own pocket totals over every account, so nothing on this screen can
  // fall out of the accounting.
  const groups = orderGroups(data.groups);
  const groupIds = groups.map((g) => g.id);
  // How to read every derived figure on this screen. Undefined on a worker that
  // predates the roll-up — every group then renders its per-asset lines exactly
  // as before, which is the point of guarding on it.
  const valuation = data.valuation;
  // The group that holds the asked-for account opens, folded or not, without changing what the operator keeps folded.
  const focusGroup = focus ? groups.find((g) => whereIs(g, valuation, focus) !== null) : undefined;
  // One rate index for the whole screen, built from every account on the desk:
  // a group's subtotal can name an asset whose account is filed under a
  // different group, and a rate is a fact about the asset, not about the group.
  const rates = assetRates(groups, valuation);
  // The strip the operator reads first: his currencies, then one figure for
  // every coin the desk holds.
  const totals = headline(data.pocket_totals ?? data.totals, rates, valuation);
  const stamp = statedStamp(valuation);
  // Money waiting for a booking, from the rows that carry the reading — said once, above the groups.
  const waiting = toBook(groups, valuation);

  const header = (
    <ScreenHeader
      title="Wallets"
      actions={<DeskSwitch value={desk} onChange={setDesk} />}
      help={
        <>
          <p>
            Crypto figures are estimates: each coin is priced from the desk&rsquo;s own trades where it has
            any, else from a stated price. &ldquo;≈&rdquo; marks a conversion, &ldquo;≥&rdquo; a floor with
            something unpriced left out; a coin that already is USDT is counted, not valued. Estimates read
            to the whole USDT. &ldquo;est.&rdquo; stands once, in the totals; on a wallet or a group it
            appears only when its price is a stated one older than six hours — amber, with its age.
          </p>
          {stamp && (
            <p>
              Stated prices · {stamp.sources} · {fmtDate(stamp.as_of)}{stamp.age ? ` · ${stamp.age}` : ''}.
            </p>
          )}
          <p>
            Quiet accounts fold behind &ldquo;+ N quiet accounts&rdquo; and stay counted in every subtotal
            — folding hides rows, never money. The history icon beside an account (on a console, drawn
            when the row is hovered) opens its money movements.
          </p>
          <p>
            An account with nothing booked yet shows at 0 when the chain or the venue shows money on
            it, with that figure under its name and on the amber &ldquo;To book&rdquo; line above the
            groups; the figure is not counted until it is booked.
          </p>
        </>
      }
    />
  );

  return (
    <FocusRail.Provider value={focus}>
    {/* ── Below lg: stacked collapsible cards, one per group ────────────── */}
    <div className="space-y-3 lg:hidden">
      {header}

      <ValuationBanner valuation={valuation} onRetry={() => q.refetch()} />

      {/* A desk with nothing at all says it once, under the title — not in a Totals card as well. */}
      {(totals.fiat.length > 0 || totals.crypto || groups.length > 0) && (
        <Section title="Totals" icon={<Coins size={14} />}>
          {totals.fiat.length === 0 && !totals.crypto
            ? <EmptyState>Nothing held.</EmptyState>
            : (
              <KeyValue>
                {totals.fiat.map(([label, amount]) => (
                  <KeyValueRow
                    key={label}
                    label={label}
                    value={<span className={`amt ${amount < 0 ? 'text-neg' : ''}`}>{fmtAmount(amount, label)}</span>}
                  />
                ))}
                {/* Every coin on the desk as ONE figure, and "est. · 8d ago"
                    directly under it. */}
                {totals.crypto && (
                  <KeyValueRow
                    label={`Crypto · ${coinsText(totals.coins)}`}
                    value={
                      <span className="block">
                        <RollUpFigure r={totals.crypto} />
                        <ValuationNote r={totals.crypto} strip />
                      </span>
                    }
                  />
                )}
              </KeyValue>
            )
          }
        </Section>
      )}

      <ToBookLine items={waiting} desk={DESK_LABEL[desk]} />

      {/* One door on the phone too (21 Sept 2026): the four tiles were the
          More sheet's doors again, a tap away on every screen. */}
      <Link to="/treasury/adjust" className="btn-secondary text-body py-2 inline-flex items-center">Adjust balance</Link>

      {groups.map((g) => (
        <MobileGroupCard key={g.id} group={g} valuation={valuation} rates={rates} desk={DESK_LABEL[desk]}
          open={!fold.isCollapsed(g.id) || g === focusGroup} onToggle={() => fold.toggle(g.id)} />
      ))}

      {groups.length === 0 && <EmptyState>Nothing held.</EmptyState>}
    </div>

    {/* ── lg+: console layout — pocket totals + one panel per group ─────── */}
    <div className="hidden lg:block space-y-6">
      {header}

      {(totals.fiat.length > 0 || totals.crypto) && (
        // items-START: the collapsed crypto column carries a second line
        // under its figure (the note), and aligning on the bottom would lift
        // that figure a line clear of the fiat ones it sits beside.
        <div className="flex flex-wrap items-start gap-x-6 gap-y-2 rounded-xl border border-stroke bg-panel px-4 py-3">
          {totals.fiat.map(([label, amount]) => (
            <div key={label} className="flex flex-col gap-0.5">
              <span className="text-label font-medium uppercase tracking-[0.06em] text-tg-hint">{label}</span>
              <span className={`font-mono tabular text-body font-medium ${amount < 0 ? 'text-neg' : ''}`}>
                {fmtAmount(amount, label)}
              </span>
            </div>
          ))}
          {/* Ten coin columns, folded into one. The count is part of the
              caption so it is visible that they were FOLDED and not dropped —
              and the wallets that hold them are two panels below. */}
          {totals.crypto && (
            <div className="flex flex-col gap-0.5 max-w-[19rem]">
              <span className="text-label font-medium uppercase tracking-[0.06em] text-tg-hint">
                Crypto · {coinsText(totals.coins)}
              </span>
              <RollUpFigure r={totals.crypto} />
              <ValuationNote r={totals.crypto} strip />
            </div>
          )}
        </div>
      )}

      <ValuationBanner valuation={valuation} onRetry={() => q.refetch()} />

      <ToBookLine items={waiting} />

      <div className="flex items-center gap-2.5">
        {/* One door: the rail beside every screen pins Move, Record and
            Money already — a second copy of each here was the clutter
            (20 Sept 2026). Adjust is the one the rail does not carry. */}
        <QuickAction to="/treasury/adjust">Adjust balance</QuickAction>
        <span className="flex-1" />
        {/* A VIEW control, not an action on the desk's money — which is why it is
            not in ActionBar and reads as plain text rather than a button. */}
        <button
          type="button"
          onClick={() => fold.setAll(groupIds, !fold.allCollapsed(groupIds))}
          className="text-meta text-tg-hint transition-colors hover:text-tg-text"
        >
          {fold.allCollapsed(groupIds) ? 'Expand all' : 'Collapse all'}
        </button>
      </div>

      {groups.map((g) => (
        <DesktopGroupPanel key={g.id} group={g} valuation={valuation} rates={rates}
          open={!fold.isCollapsed(g.id) || g === focusGroup} onToggle={() => fold.toggle(g.id)} />
      ))}

      {groups.length === 0 && <EmptyState className="py-14">Nothing held.</EmptyState>}
    </div>
    </FocusRail.Provider>
  );
}

/** Desktop ghost action — mockup qa-btn: panel surface, border, accent hover. */
function QuickAction({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center rounded-md border border-stroke bg-panel px-3.5 py-2 text-body font-medium transition-colors hover:border-tg-accent"
    >
      {children}
    </Link>
  );
}

/* ════════════════ Grouped view — phone ════════════════ */

/** An account's name on the phone, without its own desk in front of it:
 *  "DubaiOTC · Bybit" is "Bybit" on the DubaiOTC desk — the desk switch at
 *  the top already says whose accounts these are, and on a 375px row the
 *  prefix is the part that gets cut. Another desk's account keeps it. */
function phoneRailName(line: { slug?: string | null; label?: string | null }, desk: string): string {
  const name = railName(line);
  const prefix = `${desk} · `;
  return name.startsWith(prefix) ? name.slice(prefix.length) : name;
}

/** A GROUP THAT WOULD SAY ITS ONE ROW TWICE (21 Oct 2026 audit). A group with a
 *  single account in a single currency printed that account's figure in its
 *  header ("1 account · AED 52,000") and again on the row under it — and, on the
 *  phone, a third time as a bare "1,200 USDT" line or "SUBTOTAL · AED 52,000".
 *  When the ONE row is on screen under the header the header says the group's
 *  name and nothing else. It never applies to a FOLDED group (its header is the
 *  only place its money shows — folding hides rows, never money), to a group
 *  whose row sits behind the quiet fold, nor to an account holding several
 *  coins, whose header is the estimate of them all and not a repeat. A group
 *  of more than one row keeps its subtotal. */
function repeatsItsRow(group: TreasuryGroup, wallets: boolean, count: number, quietCount: number, open: boolean): boolean {
  if (!open || count !== 1 || quietCount > 0) return false;
  if (wallets || group.style === 'asset_lines') return true;
  return (group.lines[0]?.balances?.length ?? 0) === 1;
}

function MobileGroupCard({ group, valuation, rates, open, onToggle, desk }: {
  group: TreasuryGroup; valuation?: TreasuryValuation;
  rates: Map<string, AssetRate>; open: boolean; onToggle: () => void;
  /** The desk's display name (DESK_LABEL) — dropped from account names. */
  desk: string;
}) {
  // The quiet fold opens by itself when a link asked for an account behind it; a per-asset group holds an account
  // under every coin it has, so no ONE row is the account's — that group scrolls to its top instead.
  const want = useContext(FocusRail);
  const at = want ? whereIs(group, valuation, want) : null;
  const [showQuiet, setShowQuiet] = useState(at === 'quiet');
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (at && group.style === 'asset_lines') sectionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [at, group.style]);
  // Same split as the console: the phone is the daily driver, so "most
  // relevant balances, not stale accounts" applies here first. Only which
  // rows are on screen changed; the disclosure carries the true count.
  const wallets = readsAsWallets(group, valuation);
  const { lines, shown, quiet } = splitLines(group);
  const rails = splitRails(group);
  const visible = showQuiet ? [...shown, ...quiet] : shown;
  const visibleRails = showQuiet ? [...rails.shown, ...rails.quiet] : rails.shown;
  const count = wallets ? rails.rails.length : lines.length;
  const quietCount = wallets ? rails.quiet.length : quiet.length;
  // What this group is worth, the way the operator asked to read it: his
  // currencies itemised, his coins as one figure.
  const head = headline(group.subtotals, rates, valuation);
  const empty = head.fiat.length === 0 && !head.crypto;
  // One row on screen under the header: the header does not say it again.
  const single = repeatsItsRow(group, wallets, count, quietCount, open);

  return (
    <section ref={sectionRef} className="card">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between"
      >
        <span className="flex items-center gap-1.5 font-semibold text-body">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {groupIcon(group.id)}
          {group.label}
          {!single && <span className="text-tg-hint font-normal ml-1">· {count}</span>}
        </span>
        {/* Folded: one line. The "≈" in front of the figure says it is a
            valuation; "est." joins it only when the price behind it is a stale
            stated one (21 Oct 2026 audit: it was on every folded group, grey).
            The age waits inside. */}
        {!open && (empty
          ? <span className="text-tg-hint text-meta truncate ml-2">nothing held</span>
          : (
            <span className="flex items-baseline gap-1.5 ml-2 min-w-0">
              <GroupHeadline h={head} note={false} className="text-tg-hint text-meta truncate" />
              {head.crypto && head.crypto.stale && (head.crypto.kind === 'valued' || head.crypto.kind === 'partial') && (
                <span className="text-meta shrink-0 text-warn">est.</span>
              )}
            </span>
          ))}
      </button>

      {open && wallets && (
        <>
          {/* What the whole group holds: one cumulative figure, not eight
              token amounts — the quantities live one tap down, in the wallets.
              Not said for a group of one wallet: that wallet's row is the figure. */}
          {!single && (
            <div className="border-t border-tg-secondary mt-2 pt-2 text-meta text-tg-hint">
              <GroupHeadline h={head} />
            </div>
          )}
          <div className={single ? 'mt-2 space-y-0' : 'mt-1 space-y-0'}>
            {visibleRails.map((rv) => (
              <MobileWalletLine key={rv.slug} rail={rv} valuation={valuation!} />
            ))}
            {quietCount > 0 && (
              <QuietToggle
                count={quietCount}
                noun={lineNoun(group.id, quietCount, true)}
                open={showQuiet}
                onToggle={() => setShowQuiet(!showQuiet)}
              />
            )}
          </div>
        </>
      )}

      {open && !wallets && (group.style === 'asset_lines'
        ? (
          <div className="mt-2 space-y-0">
            {visible.map((l) => (
              <MobileAssetLine key={l.key} line={l} drill={group.id === 'treasury_crypto'} />
            ))}
            {quiet.length > 0 && (
              <QuietToggle
                count={quiet.length}
                noun={lineNoun(group.id, quiet.length)}
                open={showQuiet}
                onToggle={() => setShowQuiet(!showQuiet)}
              />
            )}
          </div>
        )
        : (
          <>
            {!empty && !single && (
              <div className="border-t border-tg-secondary mt-2 pt-2">
                <KeyValue>
                  {head.fiat.map(([asset, amount]) => (
                    <KeyValueRow
                      key={asset}
                      label={`Subtotal · ${asset}`}
                      value={<span className={`amt ${amount < 0 ? 'text-neg' : ''}`}>{fmtAmount(amount, asset)}</span>}
                    />
                  ))}
                  {/* A bank group's subtotal is a currency and stays one. An
                      exchange group's subtotal is coins, and collapses the same
                      way everything else on this screen does. */}
                  {head.crypto && (
                    <KeyValueRow
                      label="Subtotal"
                      value={
                        <span className="block">
                          <RollUpFigure r={head.crypto} size="sm" />
                          <ValuationNote r={head.crypto} />
                        </span>
                      }
                    />
                  )}
                </KeyValue>
              </div>
            )}
            {/* ONE ROW PER ACCOUNT (operator, 19 Sept 2026: "fix the phone
                layout for the treasury screen too"): the name once, its
                balances stacked at the right — an exchange holding five
                coins used to be five rows all called "DubaiOTC · Bybit". */}
            <div className="mt-2 divide-y divide-tg-secondary">
              {visible.map((l) => {
                const balances = l.balances ?? [];
                if (balances.length === 0) return null;
                // Money waiting for a booking: its caption stands under the name, on a line of its own.
                const waitingLine = waitingCaption(balances.filter(isOutsideOnly).map((b) => ({ asset: b.asset, outside: b.outside! })));
                return (
                  <FocusDiv key={l.key} slug={l.slug} focusClassName="pl-2" className="flex items-start justify-between gap-3 py-1.5">
                    {/* The name wraps to a second line rather than being cut ("RAK · Northern Gulf Commoditi…"). */}
                    <div className="min-w-0">
                      {/* The name is what he reads — body text, as on the console and as a wallet's name is; it was the
                          muted label face (sweep, 22 Oct 2026: bank names grey beside white wallet names). */}
                      <span className="text-body line-clamp-2 break-words pt-px" title={railName(l)}>{phoneRailName(l, desk)}</span>
                      {waitingLine && <span className="block text-meta leading-[1.45] text-tg-hint">{waitingLine}</span>}
                    </div>
                    <span className="flex items-start gap-1.5 shrink-0">
                      <span className="flex flex-col items-end">
                        {balances.map((b) => (
                          <span key={`${b.account}:${b.asset}`} className="amt text-body text-right whitespace-nowrap">
                            {fmtAmount(b.balance, b.asset)}{' '}
                            <span className="text-tg-hint">{displayAsset(b.asset)}</span>
                            {/* Same words as the console row (isOwed): a running
                                account below zero is owed, and says so. */}
                            {isOwed(l, b.balance) && (
                              <span className="block font-sans text-meta leading-[1.45] text-tg-hint">{owedToRunningAccountWords(l)}</span>
                            )}
                          </span>
                        ))}
                      </span>
                      <RailHistory slug={l.slug} className="mt-1" />
                    </span>
                  </FocusDiv>
                );
              })}
              {quiet.length > 0 && (
                <QuietToggle
                  count={quiet.length}
                  noun={lineNoun(group.id, quiet.length)}
                  open={showQuiet}
                  onToggle={() => setShowQuiet(!showQuiet)}
                />
              )}
            </div>
          </>
        )
      )}
    </section>
  );
}

/** One per-asset row on the phone — tap to unfold the wallets behind it. */
function MobileAssetLine({ line, drill }: { line: GroupLine; drill: boolean }) {
  const want = useContext(FocusRail);
  // The asset lives on the parent line, so carry it onto each source before
  // sorting: without it every wallet falls back to the default dust floor and
  // half a bitcoin sorts below a dusty AED box.
  const sources = drillSources(line);
  // Crypto always drills down to wallets; office cash only when the currency
  // sits in more than one pocket.
  const expandable = drill ? sources.length > 0 : sources.length > 1;
  const [open, setOpen] = useState(expandable && !!want && sources.some((s) => s.slug === want));
  const val = stampedValuation(line);
  const assetLabel = displayAsset(line.asset);
  const prominent = assetLabel === 'BTC';

  return (
    <div className="border-b border-tg-secondary last:border-b-0">
      <button
        onClick={expandable ? () => setOpen(!open) : undefined}
        className={`w-full flex items-baseline justify-between gap-3 py-1.5 ${expandable ? '' : 'cursor-default'}`}
      >
        <span className="flex items-center gap-1 text-meta text-tg-hint">
          {expandable && (open ? <ChevronDown size={12} /> : <ChevronRight size={12} />)}
          {assetLabel}
          {drill && sources.length > 0 && (
            <span className="opacity-70">· {sources.length === 1 ? '1 wallet' : `${sources.length} wallets`}</span>
          )}
        </span>
        <span className="text-right">
          <span className={`${prominent ? 'amt-key' : 'amt text-body'} ${(line.balance ?? 0) < 0 ? 'text-neg' : ''}`}>
            {fmtAmount(line.balance, line.asset)}
          </span>
          {val && <span className="block text-meta text-tg-hint">{fmtStampedValuation(val)}</span>}
        </span>
      </button>
      {open && sources.map((s, i) => (
        <div key={`${s.account}:${i}`} className={`flex items-baseline justify-between gap-3 pl-5 pb-1 ${s.slug === want ? FOCUS_ROW : ''}`} title={railName(s)}>
          {s.slug === want && <FocusBar />}
          <div className="min-w-0">
            <span className="block text-meta text-tg-hint truncate">{railName(s)}</span>
            {isOutsideOnly(s) && (
              <span className="block text-meta leading-[1.45] text-tg-hint">{outsideCaption(s.outside!, line.asset)}</span>
            )}
          </div>
          <span className="flex items-center gap-1.5 shrink-0">
            <span className="amt text-meta text-right">{fmtAmount(s.balance, line.asset)}</span>
            <RailHistory slug={s.slug} />
          </span>
        </div>
      ))}
    </div>
  );
}

/** ONE WALLET, ONE ROW — phone. Tap to unfold the coins it actually holds.
 *  The name and the cumulative figure sit on the row; a stale stated price's
 *  "est. · 8d ago" sits directly under the figure — a figure that scrolls away
 *  from its caveat is a figure without one. Money waiting for a booking stands
 *  under the NAME, on a line of its own, and the name wraps to a second line
 *  instead of being cut to "Hot wallet · …" (21 Oct 2026 audit). */
function MobileWalletLine({ rail, valuation }: { rail: RailRow; valuation: TreasuryValuation }) {
  const { focused, ref } = useFocusRow(rail.slug);
  const [open, setOpen] = useState(focused);
  const r = rollUp(rail, valuation);
  const held = rail.assets.length;
  const state = accountSubtitle(rail, { kind: false });
  const sub = [coinsText(held), state].filter(Boolean).join(' · ');
  // Coins at 0 on the books with money waiting outside: the caption names them all;
  // when EVERY coin waits the figure is "≈ 0" and has no estimate to qualify.
  const waiting = waitingCoins(rail);
  const allWaiting = waiting.length > 0 && waiting.length === held;

  return (
    <div ref={ref} className={`border-b border-tg-secondary last:border-b-0 ${focused ? `${FOCUS_ROW} pl-2` : ''}`}>
      {focused && <FocusBar />}
      <div className="flex items-start gap-1.5">
        <button
          onClick={() => setOpen(!open)}
          className="flex-1 min-w-0 flex items-start justify-between gap-3 py-1.5 text-left"
        >
          <span className="flex items-start gap-1 min-w-0">
            <span className="mt-[2px] shrink-0 text-tg-hint">
              {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </span>
            <span className="flex flex-col min-w-0">
              <span className="text-body font-medium line-clamp-2 break-words" title={railName(rail)}>{railName(rail)}</span>
              <span className="text-meta text-tg-hint">{sub}</span>
              {waiting.length > 0 && <span className="text-meta leading-[1.45] text-tg-hint">{waitingCaption(waiting)}</span>}
            </span>
          </span>
          <span className="text-right shrink-0">
            <RollUpFigure r={r} />
            {!allWaiting && <ValuationNote r={r} />}
          </span>
        </button>
        <RailHistory slug={rail.slug} className="mt-2" />
      </div>
      {open && <WalletCoins rail={rail} valuation={valuation} dense />}
    </div>
  );
}

/* ════════════════ Grouped view — desktop console ════════════════ */

function DesktopGroupPanel({ group, valuation, rates, open, onToggle }: {
  group: TreasuryGroup; valuation?: TreasuryValuation;
  rates: Map<string, AssetRate>; open: boolean; onToggle: () => void;
}) {
  // See MobileGroupCard: a link opens the quiet fold it needs, and a per-asset group scrolls to its top.
  const want = useContext(FocusRail);
  const at = want ? whereIs(group, valuation, want) : null;
  const [showQuiet, setShowQuiet] = useState(at === 'quiet');
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (at && group.style === 'asset_lines') sectionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [at, group.style]);
  const wallets = readsAsWallets(group, valuation);
  const { lines, shown, quiet } = splitLines(group);
  const rails = splitRails(group);
  const visible = showQuiet ? [...shown, ...quiet] : shown;
  const visibleRails = showQuiet ? [...rails.shown, ...rails.quiet] : rails.shown;
  const count = wallets ? rails.rails.length : lines.length;
  const quietCount = wallets ? rails.quiet.length : quiet.length;
  // Header count and headline are over ALL lines, collapsed ones included —
  // Treasury must foot whatever the operator has expanded.
  const head = headline(group.subtotals, rates, valuation);
  // One row on screen under the header: the header does not say it again.
  const single = repeatsItsRow(group, wallets, count, quietCount, open);

  return (
    <section ref={sectionRef}>
      {/* The whole header is the hit target, and the count and subtotal stay put
          whether the group is open or closed — so a folded group still foots.
          (21 Oct 2026 audit: a group whose one row is open beneath it is the
          exception — its count and total would be that row, said twice.) */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-baseline gap-2.5 mb-2.5 px-0.5 text-left group/hdr"
      >
        <h2 className="flex items-center gap-1.5 text-meta font-medium uppercase tracking-[0.09em] text-tg-hint transition-colors group-hover/hdr:text-tg-text">
          <span className="w-3.5 shrink-0">
            {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </span>
          <span className="opacity-70">{groupIcon(group.id)}</span>
          {group.label}
        </h2>
        {!single && <span className="font-mono tabular text-meta text-tg-hint">{countText(group.id, count, wallets)}</span>}
        <span className="flex-1" />
        {/* THE HEADLINE: the group's currencies, then one cumulative estimate
            — with "est. · 8d ago" beneath when its price is a stale stated
            one. Right-aligned and wrapped rather than truncated: the note is
            the half that makes the figure safe to read. */}
        {!single && (
          <GroupHeadline
            h={head}
            className="max-w-[58%] text-right text-meta leading-[1.5] text-tg-hint"
          />
        )}
      </button>
      {open && (
      <div className="rounded-xl border border-stroke bg-panel divide-y divide-stroke-soft overflow-hidden">
        {wallets
          ? visibleRails.map((rv) => <DesktopWalletLine key={rv.slug} rail={rv} valuation={valuation!} />)
          : group.style === 'asset_lines'
            ? visible.map((l) => <DesktopAssetLine key={l.key} line={l} drill={group.id === 'treasury_crypto'} />)
            : visible.map((l) => <DesktopRailLine key={l.key} line={l} kind={!KIND_IN_HEADING.has(group.id)} />)}
        {quietCount > 0 && (
          <QuietToggle
            count={quietCount}
            noun={lineNoun(group.id, quietCount, wallets)}
            open={showQuiet}
            onToggle={() => setShowQuiet(!showQuiet)}
          />
        )}
      </div>
      )}
    </section>
  );
}

/** The groups whose HEADING already names what their accounts are — UAE banks,
 *  India banks, India cash, Exchanges. A row inside one does not print the kind
 *  again under its name ("Bank", "Bank", "Bank"…): lib/naming's railSubtitle
 *  has `kind: false` for exactly this, and the wallet rows already use it
 *  (21 Oct 2026 audit). "Acquired accounts" and "Other accounts" name no kind,
 *  so their rows keep it. */
const KIND_IN_HEADING = new Set(['uae_banks', 'india_banks', 'india_cash', 'exchanges']);

/** ONE WALLET, ONE ROW — console. The row the operator asked for: his Ledger is
 *  one line reading what the whole device is worth, and one click opens the six
 *  coins with their real quantities. Same component shape as DesktopAssetLine,
 *  transposed — chevron, name and state on the left, figure on the right, the
 *  drill-down underneath. The history link sits OUTSIDE the toggle button: an
 *  anchor inside a button is not a row, it is two controls fighting. */
function DesktopWalletLine({ rail, valuation }: { rail: RailRow; valuation: TreasuryValuation }) {
  // A link to this wallet opens it: the coins it holds are the answer the link was after.
  const { focused, ref } = useFocusRow(rail.slug);
  const [open, setOpen] = useState(focused);
  const r = rollUp(rail, valuation);
  const held = rail.assets.length;
  const state = accountSubtitle(rail, { kind: false });
  const sub = [coinsText(held), state].filter(Boolean).join(' · ');
  // Coins at 0 on the books with money waiting outside: the caption names them all;
  // when EVERY coin waits the figure is "≈ 0" and has no estimate to qualify.
  const waiting = waitingCoins(rail);
  const allWaiting = waiting.length > 0 && waiting.length === held;

  return (
    <div ref={ref} className={focused ? FOCUS_ROW : undefined}>
      {focused && <FocusBar />}
      <div className="group/row flex items-start gap-2 px-4 py-2.5 transition-colors hover:bg-panel-2">
        <button
          onClick={() => setOpen(!open)}
          className="flex-1 min-w-0 flex items-start justify-between gap-4 text-left"
        >
          <span className="flex items-start gap-2 min-w-0">
            <span className="w-3.5 shrink-0 pt-[3px] text-tg-hint">
              {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </span>
            <span className="flex flex-col min-w-0">
              <span className="text-body font-medium truncate" title={railName(rail)}>{railName(rail)}</span>
              <span className="text-meta text-tg-hint">{sub}</span>
              {waiting.length > 0 && <span className="text-meta leading-[1.45] text-tg-hint">{waitingCaption(waiting)}</span>}
            </span>
          </span>
          <span className="text-right shrink-0">
            <RollUpFigure r={r} size="lg" />
            {!allWaiting && <ValuationNote r={r} />}
          </span>
        </button>
        <RailHistory slug={rail.slug} className="mt-1.5" quiet />
      </div>
      {open && <WalletCoins rail={rail} valuation={valuation} />}
    </div>
  );
}

/** Per-asset line: quantity leads (BTC extra-prominent), valuation subordinate
 *  when a rate is known, wallets one click away. */
function DesktopAssetLine({ line, drill }: { line: GroupLine; drill: boolean }) {
  const want = useContext(FocusRail);
  const sources = drillSources(line);
  const expandable = drill ? sources.length > 0 : sources.length > 1;
  // A link to one account unfolds the coins it holds and marks its row inside them.
  const [open, setOpen] = useState(expandable && !!want && sources.some((s) => s.slug === want));
  const val = stampedValuation(line);
  const assetLabel = displayAsset(line.asset);
  const prominent = assetLabel === 'BTC';

  return (
    <div>
      <button
        onClick={expandable ? () => setOpen(!open) : undefined}
        className={`w-full flex items-center justify-between gap-4 px-4 py-2.5 text-left ${expandable ? 'transition-colors hover:bg-panel-2' : 'cursor-default'}`}
      >
        <span className="flex items-center gap-2 min-w-0">
          <span className="w-3.5 shrink-0 text-tg-hint">
            {expandable && (open ? <ChevronDown size={13} /> : <ChevronRight size={13} />)}
          </span>
          <span className="flex flex-col min-w-0">
            <span className="text-body font-medium">{assetLabel}</span>
            {drill && sources.length > 0 && (
              <span className="text-meta text-tg-hint">
                {sources.length === 1 ? '1 wallet' : `${sources.length} wallets`}
              </span>
            )}
          </span>
        </span>
        <span className="text-right shrink-0">
          <span className={`${prominent ? 'amt-key text-title' : 'amt text-body'} ${(line.balance ?? 0) < 0 ? 'text-neg' : ''}`}>
            {fmtAmount(line.balance, line.asset)}
          </span>
          {val && <span className="block text-meta text-tg-hint">{fmtStampedValuation(val)}</span>}
        </span>
      </button>
      {open && sources.length > 0 && (
        <div className="px-4 pb-2.5">
          {sources.map((s, i) => (
            <div key={`${s.account}:${i}`} className={`group/row flex items-baseline justify-between gap-4 pl-6 py-1 ${s.slug === want ? FOCUS_ROW : ''}`} title={railName(s)}>
              {s.slug === want && <FocusBar />}
              <div className="min-w-0">
                <span className="block text-meta text-tg-hint truncate">{railName(s)}</span>
                {isOutsideOnly(s) && (
                  <span className="block text-meta leading-[1.45] text-tg-hint">{outsideCaption(s.outside!, line.asset)}</span>
                )}
              </div>
              <span className="flex items-center gap-2 shrink-0">
                <span className={`amt text-meta text-right ${s.balance < 0 ? 'text-neg' : ''}`}>{fmtAmount(s.balance, line.asset)}</span>
                <RailHistory slug={s.slug} quiet />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** A RUNNING ACCOUNT BELOW ZERO IS OWED, NOT WRONG.
 *
 *  The angadiya is an account the desk draws on and settles later (operator,
 *  3 Sept 2026), so its balance legitimately runs negative — that is money the
 *  desk owes it, the chart's design, and the Sheet already files it as "Owed to
 *  running accounts". This screen painted the same figure in the red that
 *  everywhere else means "a withdrawal was booked twice", bare (6 Sept:
 *  "−437,590 INR"). Which accounts run this way is the BFF's rule
 *  (lib/buckets.ts RUNNING_ACCOUNT_RAILS), read off the line's
 *  `running_account` flag; the words are lib/naming's. Both renderers of an
 *  account line — the console row and the phone row — ask here, so they agree. */
function isOwed(line: GroupLine, balance: number): boolean {
  return line.running_account === true && balance < 0;
}

/** One account (bank / exchange / box) with its balances stacked on the right.
 *  The asset chip and the amount live in `trailing` — never string-joined into
 *  the name, which is what produced `Wio · Shweta · USDT·TRC` elsewhere.
 *  Padding is trimmed to `py-1` because `<Name>` carries its own 32px floor.
 *
 *  `kind`: print the kind ("Bank") under the name. False inside a group whose
 *  heading already says it. Under the name too, on the same line, stands what
 *  waits for a booking on the account — its caption, off the figure's column. */
function DesktopRailLine({ line, kind }: { line: GroupLine; kind: boolean }) {
  const balances = line.balances ?? [];
  const waitingLine = waitingCaption(balances.filter(isOutsideOnly).map((b) => ({ asset: b.asset, outside: b.outside! })));
  const secondary = [accountSubtitle(line, { kind }), waitingLine].filter(Boolean).join(' · ');
  return (
    // The history link sits OUTSIDE <Name>, beside it — the name component owns
    // primary/secondary/trailing and nothing else may be threaded through it.
    <FocusDiv slug={line.slug} className="group/row px-4 py-1 flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <Name
          primary={railName(line)}
          secondary={secondary}
          trailing={balances.map((b) => (
            <span key={`${b.account}:${b.asset}`} className={`block ${isOwed(line, b.balance) || b.balance >= 0 ? '' : 'text-neg'}`}>
              {fmtAmount(b.balance, b.asset)}{' '}
              <span className="text-meta text-tg-hint tracking-[0.02em]">{displayAsset(b.asset)}</span>
              {isOwed(line, b.balance) && (
                <span className="block font-sans text-meta leading-[1.45] text-tg-hint">{owedToRunningAccountWords(line)}</span>
              )}
            </span>
          ))}
        />
      </div>
      <RailHistory slug={line.slug} quiet />
    </FocusDiv>
  );
}
